// src/features/plan/PlanCompletenessCell.jsx
//
// PLACES-MARKETS-DRAWER (amendment #2, Corey 2026-10-31): 4-dot market plan
// completeness for the Markets Plan column. The dots are the four persisted
// market-plan fields — Strategy, Target date, Owner, Note. The former fifth
// "all offices planned" gate is dropped here; Portfolio Command's separate
// office-based Plan Coverage metric is unaffected.
//
// The same pure counter (countMarketPlanFields) drives the drawer's
// "n of 4 set" and the Markets "Plans complete" KPI so all three agree.

import React from "react";
import { ProgressDots } from "../../v2/components/ProgressDots.jsx";

export const PLAN_FIELD_TOTAL = 4;
export const PLAN_FIELD_LABELS = ["Strategy", "Target date", "Owner", "Note"];

function isSet(val) {
  return val !== null && val !== undefined && String(val).trim() !== "";
}

/** Pure: which of the four persisted market-plan fields are set. */
export function marketPlanFieldFlags(market = {}) {
  return [
    isSet(market.strategy),
    isSet(market.strategyTargetDate ?? market.target_date),
    isSet(market.owner),
    isSet(market.planNote ?? market.plan_note),
  ];
}

/** Pure: count of set fields (0-4). */
export function countMarketPlanFields(market) {
  return marketPlanFieldFlags(market).filter(Boolean).length;
}

/** Pure: plans complete across a market population (all four set). */
export function countCompletePlans(markets = []) {
  return markets.filter((m) => countMarketPlanFields(m) === PLAN_FIELD_TOTAL).length;
}

export function PlanCompletenessCell({
  strategy, strategyTargetDate, target_date, owner, planNote, plan_note, isCompact = false, inline = false,
}) {
  const flags = marketPlanFieldFlags({ strategy, strategyTargetDate, target_date, owner, planNote, plan_note });
  const count = flags.filter(Boolean).length;

  const missing = PLAN_FIELD_LABELS.filter((_, i) => !flags[i]);
  const statusLabel = missing.length === 1 ? `${missing[0]} missing` : `${count} of ${PLAN_FIELD_TOTAL}`;

  const tooltip = PLAN_FIELD_LABELS.map((l, i) => `${i + 1}. ${l}: ${flags[i] ? "set" : "not set"}`).join("\n");

  return (
    <div data-testid="plan-completeness-cell" data-count={count} style={{ minWidth: 0, textAlign: "center",
      ...(isCompact && { display: "flex", alignItems: "center", justifyContent: "center" }) }}>
      <ProgressDots steps={flags.map((on, i) => ({ key: PLAN_FIELD_LABELS[i], state: on ? "done" : "upcoming" }))}
        complete={count === PLAN_FIELD_TOTAL} compact={isCompact || inline} caption={statusLabel}
        shortCaption={`${count}/${PLAN_FIELD_TOTAL}`} tooltip={`${statusLabel}\n${tooltip}`}
        dotsTestId="plan-completeness-dots" captionTestId="plan-completeness-label"
        dotTestId={i => `plan-dot-${i}`} />
    </div>
  );
}
